import * as ExcelJS from "exceljs/dist/exceljs.min.js";

export interface ExcelCleanerConfig {
  headerRow?: number;
  dataStartRow?: number;
  maxDataRows?: number;
  sheetIndex?: number;
}

function normalizeExcelCellValue(value: any): any {
  if (value === null || value === undefined) return null;

  if (typeof value === "object" && "formula" in value) {
    return normalizeExcelCellValue(value.result);
  }

  if (typeof value === "object" && "richText" in value) {
    const text = value.richText
      ?.map((item: any) => item.text || "")
      .join("");

    return normalizeExcelCellValue(text);
  }

  if (typeof value === "object" && "text" in value) {
    return normalizeExcelCellValue(value.text);
  }

  if (typeof value === "string") {
    const cleaned = value
      .replace(/\u00A0/g, " ")
      .replace(/[\u200B-\u200D\uFEFF]/g, "")
      .trim();

    return cleaned === "" ? null : cleaned;
  }

  return value;
}

function getLastHeaderColumn(
  worksheet: any,
  headerRowNumber: number,
): number {
  const headerRow = worksheet.getRow(headerRowNumber);
  let lastColumn = 0;

  headerRow.eachCell(
    { includeEmpty: false },
    (cell: any, colNumber: number) => {
      const value = normalizeExcelCellValue(cell.value);

      if (value !== null && value !== "") {
        lastColumn = colNumber;
      }
    },
  );

  return lastColumn || worksheet.columnCount;
}

function isWorksheetRowEmpty(
  worksheet: any,
  rowNumber: number,
  lastColumn: number,
): boolean {
  const row = worksheet.getRow(rowNumber);

  for (let col = 1; col <= lastColumn; col++) {
    const cell = row.getCell(col);
    const normalizedValue = normalizeExcelCellValue(cell.value);

    if (normalizedValue !== null && normalizedValue !== "") {
      return false;
    }
  }

  return true;
}

function findLastRowToReview(
  worksheet: any,
  dataStartRow: number,
  maxDataRows?: number,
): number {
  if (!maxDataRows) {
    return worksheet.rowCount;
  }

  return Math.min(
    worksheet.rowCount,
    dataStartRow + maxDataRows - 1,
  );
}

export async function cleanExcelFileKeepingStructure(
  file: File,
  config: ExcelCleanerConfig = {},
): Promise<File> {
  const headerRow = config.headerRow ?? 4;
  const dataStartRow = config.dataStartRow ?? 5;
  const maxDataRows = config.maxDataRows ?? 300;
  const sheetIndex = config.sheetIndex ?? 0;

  const workbook = new ExcelJS.Workbook();

  const buffer = await file.arrayBuffer();

  await workbook.xlsx.load(buffer);

  const worksheet = workbook.worksheets[sheetIndex];

  if (!worksheet) {
    return file;
  }

  const lastColumn = getLastHeaderColumn(worksheet, headerRow);

  const lastRowToReview = findLastRowToReview(
    worksheet,
    dataStartRow,
    maxDataRows,
  );

  for (
    let rowNumber = lastRowToReview;
    rowNumber >= dataStartRow;
    rowNumber--
  ) {
    const isEmpty = isWorksheetRowEmpty(
      worksheet,
      rowNumber,
      lastColumn,
    );

    if (isEmpty) {
      worksheet.spliceRows(rowNumber, 1);
    }
  }

  const cleanedBuffer = await workbook.xlsx.writeBuffer();

  return new File(
    [cleanedBuffer as BlobPart],
    file.name,
    {
      type:
        file.type ||
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    },
  );
}